import { TruckDecorator, DriverDecorator } from 'decorators'
import { omit, concat, map, set } from 'lodash'
import { http, queryString } from 'utils'

// Truck mutation imports.
import {
  SET_TRUCKS, SET_TRUCKS_PAGINATION, ADD_TRUCKS, SET_TRUCK, RESET_TRUCK,
  SET_TRUCK_ATTRIBUTE, ADD_TRUCK_CONFIGURATION, REMOVE_TRUCK_CONFIGURATION,
  PUSH_TRUCK
} from 'vx/mutations'

const initialTruckState = () => {
  return {
    id: null,
    user_id: null,
    location_id: null,
    driver_id: null,
    driver: {},
    location: {},
    name: null,
    registration: null,
    make: null,
    model: null,
    year: null,
    configurations: [],
    photo_storage_path: null,
    photo_name: null
  }
}

// Truck state.
const state = {
  trucks: [],
  truck: initialTruckState()
}

// Truck state getters.
const getters = {
  /**
   * The truck payload excludes the relationship objects.
   */
  truckPayload: state => omit(state.truck, ['location', 'driver']),

  /**
   * Trucks decorated via the TruckDecorator.
   */
  trucksDecorated: state => map(state.trucks, truck => (new TruckDecorator(truck))),

  /**
   * The truck driver object decorated via the DriverDecorator.
   */
  driverDecorated: state => (new DriverDecorator(state.truck.driver)),

  /**
   * Truck options for drop down.
   */
  truckOptions: state => map(state.trucks, truck => ({ id: truck.id, text: truck.registration })),

  /**
   * Truck options for drop down.
   */
  truckOptionsWithNone: (state, getters) => concat([{ id: '', text: '<em>None</em>' }], getters.truckOptions)
}

// Truck actions.
const actions = {
  /**
   * Get a list of trucks based on user.
   */
  getTrucks ({ commit }, payload) {
    return http.get(`trucks?${queryString(payload)}`).then(response => {
      commit(SET_TRUCKS, response.data.data)
      commit(SET_TRUCKS_PAGINATION, omit(response.data, 'data'))

      return response
    })
  },

  /**
   * Get the trucks for the next page.
   */
  getTrucksForNextPage ({ commit, state }, payload) {
    if (state.pagination.current_page === state.pagination.last_page || state.pagination.total === 0) {
      return Promise.reject('There are no more trucks to load.')
    }

    let page = state.pagination.current_page + 1

    return http.get(`trucks?${queryString(payload)}&page=${page}`).then(response => {
      commit(ADD_TRUCKS, response.data.data)
      commit(SET_TRUCKS_PAGINATION, omit(response.data, 'data'))

      return response
    })
  },

  /**
  * Store a new truck.
  */
  storeTruck ({ commit }, payload) {
    return http.post(`trucks`, payload).then(response => {
      return response
    })
  },

  /**
   * Update an existing truck's data.
   */
  updateTruck ({ commit }, {id, payload}) {
    return http.put(`trucks/${id}`, payload).then(response => {
      commit(SET_TRUCK, response.data)

      return response
    })
  },

  /**
   * Retrieve a truck by ID
   */
  getTruck ({ commit, state }, id) {
    return http.get(`trucks/${id}`).then(response => {
      commit(SET_TRUCK, response.data)

      return state.truck
    })
  },

  /**
   * Delete a truck.
   */
  deleteTruck ({ commit }, id) {
    return http.delete(`trucks/${id}`).then(response => {
      commit(SET_TRUCK, initialTruckState())

      return response
    })
  },

  /**
   * Upload a truck photo and crop dimensions.
   */
  uploadTruckPhoto (store, { id, file, crop }) {
    let payload = new FormData()

    payload.append('photo', file)
    payload.append('crop[x]', crop.x)
    payload.append('crop[y]', crop.y)
    payload.append('crop[width]', crop.width)
    payload.append('crop[height]', crop.height)

    return http.post(`trucks/${id}/photos`, payload)
  },

  /**
   * Get a truck photos data URI for a given dimension.
   */
  getTruckPhotoDataUri (store, { id, dimension }) {
    return http.get(`trucks/${id}/photos/${dimension}`)
  }
}

// Truck state mutations.
const mutations = {
  /**
  * Set the trucks.
  */
  [SET_TRUCKS] (state, trucks) {
    state.trucks = trucks
  },

  /**
   * Add more trucks to the trucks state.
   */
  [ADD_TRUCKS] (state, trucks) {
    state.trucks = concat(state.trucks, trucks)
  },

  /**
   * Set the truck pagination.
   */
  [SET_TRUCKS_PAGINATION] (state, pagination) {
    state.pagination = pagination
  },

  /**
   * Set the state truck.
   */
  [SET_TRUCK] (state, truck) {
    state.truck = truck
  },

  /**
   * Push a truck.
   */
  [PUSH_TRUCK] (state, truck) {
    state.trucks.push(truck)
  },

  /**
   * Reset the truck.
   */
  [RESET_TRUCK] (state) {
    state.truck = initialTruckState()
  },

  /**
   * Set trucks attribute.
   */
  [SET_TRUCK_ATTRIBUTE] (state, { attribute, value }) {
    set(state.truck, attribute, value)
  },

  /**
   * Add configuration to the trucks configurations array
   */
  [ADD_TRUCK_CONFIGURATION] (state, configuration) {
    state.truck.configurations.push(configuration)
  },

  /**
   * Remove a configuration from the trucks configurations array
   */
  [REMOVE_TRUCK_CONFIGURATION] (state, configurationIndex) {
    state.truck.configurations.splice(configurationIndex, 1)
  }
}

import configuration from 'vx/modules/truck/configuration'

const modules = {
  configuration
}

// Export our state, getters, actions, and mutations.
export default {
  namespaced: true, state, getters, actions, mutations, modules
}



// WEBPACK FOOTER //
// ./src/vuex/modules/truck.js